import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getAllCategories } from '../services/categoryService';
import { getServicesByCategory, getServicesBySubcategory } from '../services/serviceService';
import { FaFilter, FaYoutube, FaTimes } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const CategoryPage = () => {
  const { categorySlug, subcategorySlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [category, setCategory] = useState(null);
  const [subcategory, setSubcategory] = useState(null);
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 50000]);
  const [sortBy, setSortBy] = useState('price-asc');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    const fetchCategoryAndServices = async () => {
      setLoading(true);
      setError('');
      try {
        // Fetch all categories
        const categories = await getAllCategories(true);
        
        // Find the current category and subcategory
        const foundCategory = categories.find(cat => cat.slug === categorySlug);
        
        if (!foundCategory) {
          setError('Category not found');
          setLoading(false);
          return;
        }
        
        setCategory(foundCategory);
        
        // Find subcategory if specified
        let foundSubcategory = null;
        if (subcategorySlug) {
          // Try different approaches to match the subcategory
          // 1. First try to match by slug
          foundSubcategory = foundCategory.subcategories?.find(
            sub => sub.slug === subcategorySlug
          );
          
          // 2. If not found, try to match by name converted to slug
          if (!foundSubcategory) {
            foundSubcategory = foundCategory.subcategories?.find(sub => {
              const subName = typeof sub === 'string' ? sub : sub.name;
              const subSlug = subName.toLowerCase().replace(/\s+/g, '-');
              return subSlug === subcategorySlug;
            });
          }
          
          // 3. If still not found, try to match by ID
          if (!foundSubcategory) {
            foundSubcategory = foundCategory.subcategories?.find(sub => 
              sub.id === subcategorySlug || 
              (typeof sub === 'object' && sub.id === subcategorySlug)
            );
          }
          
          console.log('Found subcategory:', foundSubcategory);
          setSubcategory(foundSubcategory || null);
        }
        
        // Fetch services based on category/subcategory
        let servicesData = [];
        
        if (subcategorySlug && foundSubcategory) {
          // Determine the subcategory ID or name to use
          const subcategoryIdOrName = 
            (typeof foundSubcategory === 'object' && foundSubcategory.id) ? 
              foundSubcategory.id : 
              (typeof foundSubcategory === 'string' ? 
                foundSubcategory : 
                foundSubcategory.name);
          
          // Use the proper service function to get services by subcategory
          console.log('Fetching services for subcategory:', 
            typeof foundSubcategory === 'string' ? 
              foundSubcategory : 
              foundSubcategory.name,
            'with ID/name:', subcategoryIdOrName
          );
          
          servicesData = await getServicesBySubcategory(
            foundCategory.id, 
            subcategoryIdOrName,
            true
          );
        } else {
          // Get all services in this category
          console.log('Fetching all services for category:', foundCategory.name);
          servicesData = await getServicesByCategory(foundCategory.id, true);
        }
        
        console.log(`Found ${servicesData.length} services`);
        setServices(servicesData);
        setFilteredServices(servicesData);
      } catch (err) {
        console.error('Error fetching category and services:', err);
        setError('Failed to load category data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCategoryAndServices();
  }, [categorySlug, subcategorySlug]);

  // Apply filters and sorting
  useEffect(() => {
    if (!services.length) return;
    
    let filtered = [...services];
    
    // Apply price filter
    filtered = filtered.filter(
      service => service.price >= priceRange[0] && service.price <= priceRange[1]
    );
    
    // Apply sorting
    switch (sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        break;
    }
    
    setFilteredServices(filtered);
  }, [services, priceRange, sortBy]);

  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    setPriceRange(prev => 
      name === 'min' 
        ? [parseInt(value), prev[1]] 
        : [prev[0], parseInt(value)]
    );
  };

  const resetFilters = () => {
    setPriceRange([0, 50000]);
    setSortBy('price-asc');
  };

  // Helper function to extract YouTube video ID from URL
  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    
    // Match YouTube URL patterns
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return (match && match[2].length === 11) ? match[2] : null;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
          <p className="text-yellow-700">Category not found</p>
        </div>
      </div>
    );
  }

  // Generate title based on category and subcategory
  const pageTitle = subcategory 
    ? `${subcategory.name} ${category.name}`
    : category.name;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-pink-600 mb-2">{pageTitle}</h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          {subcategory?.description || category.description}
        </p>
      </div>
      
      {/* Filters and Sorting */}
      <div className="bg-white shadow mb-8 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200"
            >
              <FaFilter className="text-gray-600" />
              <span>Filters</span>
            </button>
            
            <span className="text-sm text-gray-500">
              {filteredServices.length} services found
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <label htmlFor="sortBy" className="text-sm text-gray-600">Sort by:</label>
            <select
              id="sortBy"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
            >
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="name-asc">Name: A to Z</option>
              <option value="name-desc">Name: Z to A</option>
            </select>
          </div>
        </div>
        
        {/* Filter section - conditionally shown */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 ">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-800">Refine Results</h3>
              <button 
                onClick={resetFilters}
                className="text-pink-500 hover:text-pink-700 text-sm font-medium"
              >
                Reset All
              </button>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Price Range (₹)</h4>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  name="min"
                  value={priceRange[0]}
                  onChange={handlePriceChange}
                  min="0"
                  max={priceRange[1]}
                  className="border rounded w-24 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="number"
                  name="max"
                  value={priceRange[1]}
                  onChange={handlePriceChange}
                  min={priceRange[0]}
                  className="border rounded w-24 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* YouTube Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl bg-black">
            <button 
              onClick={() => setSelectedVideo(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <FaTimes size={24} />
            </button>
            <div className="relative pb-[56.25%] h-0">
              <iframe 
                className="absolute top-0 left-0 w-full h-full"
                src={`https://www.youtube.com/embed/${getYoutubeVideoId(selectedVideo)}`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
      
      {/* Services Grid */}
      {filteredServices.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {filteredServices.map(service => (
            <ServiceCard 
              key={service.id} 
              service={service} 
              onVideoClick={(videoUrl) => {
                setSelectedVideo(videoUrl);
              }}
            />
          ))}
        </div>
      ) : (
        <div className="bg-gray-50 p-8 text-center rounded-lg">
          <p className="text-gray-600">No services found matching your criteria.</p>
          <button 
            onClick={resetFilters}
            className="mt-2 text-pink-500 hover:text-pink-700 font-medium"
          >
            Reset Filters
          </button>
        </div>
      )}
    </div>
  );
};

// Service Card Component
const ServiceCard = ({ service, onVideoClick }) => {
  const originalPrice = service.price || 0;
  const hasDiscount = service.discount && service.discount > 0;
  const discountedPrice = hasDiscount
    ? Math.round(originalPrice - (originalPrice * service.discount / 100))
    : originalPrice;
  const discountPercentage = hasDiscount ? `-${service.discount}%` : '';

  // Check if service has a YouTube video
  const hasVideo = service.youtubeUrl && service.youtubeUrl.trim() !== '';
  
  const handleVideoClick = (e) => {
    if (hasVideo) {
      e.preventDefault();
      e.stopPropagation();
      onVideoClick(service.youtubeUrl);
    }
  };

  return (
    <Link to={`/booking/${service.id}`}>
      <motion.div 
        className="bg-white shadow-md overflow-hidden cursor-pointer"
        whileHover={{ y: -5, transition: { duration: 0.2 } }}
      >
        <div className="h-48 overflow-hidden relative">
          {service.images && service.images.length > 0 ? (
            <img 
              src={service.images[0]} 
              alt={service.name} 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-400">No Image</span>
            </div>
          )}
          
          {/* YouTube Video Icon */}
          {hasVideo && (
            <>
              {/* Small indicator icon */}
              <div className="absolute top-2 left-2 bg-red-600 text-white p-1.5 rounded-full shadow-md">
                <FaYoutube size={14} />
              </div>
              
              {/* Clickable play button */}
              <button
                onClick={handleVideoClick}
                className="absolute bottom-2 right-2 bg-red-600 text-white p-2 rounded-full shadow-md hover:bg-red-700 transition-colors z-10"
              >
                <FaYoutube size={20} />
              </button>
            </>
          )}
        </div>
        
        <div className="p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-1">{service.name}</h3>
          
          {service.discount && service.discount > 0 ? (
            <div className="mb-2">
              <p className="text-pink-600 font-bold">
                ₹{Math.round((service.price || 0) - ((service.price || 0) * service.discount / 100)).toLocaleString('en-IN')}
              </p>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500 line-through">₹{(service.price || 0).toLocaleString('en-IN')}</span>
                <span className="text-green-600 font-semibold">-{service.discount}%</span>
              </div>
            </div>
          ) : (
            <p className="text-pink-600 font-bold mb-2">₹{(service.price || 0).toLocaleString('en-IN')}</p>
          )}
          
          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
            {service.description}
          </p>
        </div>
      </motion.div>
    </Link>
  );
};

export default CategoryPage; 